import React, { useState, useEffect } from "react";
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { dt } from "../../../../shared/themes/redesignTokens";
import { FormTextField, FormTextarea, FormFieldLabel } from "../../../../shared/components/redesign/forms";
import { WorkOrderDetail } from "../../models/models";
import useTranslation from "../../../../shared/hooks/useTranslation";
import WorkOrderPriorityBadge from "../WorkOrderPriorityBadge";

interface EscalateDialogProps {
  open: boolean;
  workOrder: WorkOrderDetail;
  onConfirm: (issue: string, description: string) => Promise<void>;
  onCancel: () => void;
  loading: boolean;
}

const EscalateDialog = ({ open, workOrder, onConfirm, onCancel, loading }: EscalateDialogProps) => {
  const { translate } = useTranslation("operations");
  const [issue, setIssue] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (open && workOrder) {
      setIssue(workOrder.title || "");
      setDescription(
        `Escalated from Work Order ${workOrder.number}\n\n${workOrder.description || ""}`
      );
    }
  }, [open, workOrder]);

  const handleConfirm = async () => {
    await onConfirm(issue, description);
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: dt.radius,
          border: `1px solid ${dt.cardBorder}`,
          boxShadow: dt.cardShadowHover,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontFamily: dt.font,
          fontSize: "16px",
          fontWeight: 700,
          color: dt.textPrimary,
          borderBottom: `1px solid ${dt.rowBorder}`,
          padding: "16px 20px",
        }}
      >
        {translate("escalation.title")}
        <IconButton size="small" onClick={onCancel} sx={{ color: dt.textMuted }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: "20px" }}>
        <Box sx={{ fontFamily: dt.font, fontSize: "13px", color: dt.textSecondary, marginBottom: "16px", lineHeight: "20px" }}>
          {translate("escalation.description")}
        </Box>

        {/* Work order info summary */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "6px",
            padding: "12px 14px",
            backgroundColor: dt.pageBg,
            border: `1px solid ${dt.rowBorder}`,
            borderRadius: dt.radiusSm,
            marginBottom: "16px",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ fontFamily: dt.fontMono, fontSize: "12px", color: dt.blue, fontWeight: 600 }}>
              {workOrder.number}
            </Box>
            <WorkOrderPriorityBadge priorityCode={workOrder.priorityCode} />
          </Box>
          {workOrder.machineName && (
            <Box sx={{ fontFamily: dt.font, fontSize: "12px", color: dt.textSecondary }}>
              {workOrder.machineName}
            </Box>
          )}
        </Box>

        {/* Issue field */}
        <Box sx={{ marginBottom: "14px" }}>
          <FormFieldLabel>{translate("escalation.issue")}</FormFieldLabel>
          <FormTextField
            value={issue}
            onChange={(val: string) => setIssue(val)}
            fullWidth
          />
        </Box>

        {/* Description field */}
        <Box>
          <FormFieldLabel>{translate("escalation.descriptionField")}</FormFieldLabel>
          <FormTextarea
            value={description}
            onChange={(val: string) => setDescription(val)}
            rows={5}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          padding: "12px 20px",
          borderTop: `1px solid ${dt.rowBorder}`,
          gap: "8px",
        }}
      >
        <Button
          onClick={onCancel}
          disabled={loading}
          sx={{
            fontFamily: dt.font,
            fontSize: "13px",
            textTransform: "none",
            color: dt.textSecondary,
            borderRadius: dt.radiusSm,
          }}
        >
          {translate("escalation.cancel")}
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={loading || !issue.trim()}
          sx={{
            fontFamily: dt.font,
            fontSize: "13px",
            textTransform: "none",
            color: "#F39C12",
            border: "1px solid rgba(243, 156, 18, 0.3)",
            borderRadius: dt.radiusSm,
            padding: "6px 16px",
            "&:hover": { backgroundColor: "rgba(243, 156, 18, 0.08)" },
            "&.Mui-disabled": { opacity: 0.5 },
          }}
        >
          {loading ? "..." : translate("escalation.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EscalateDialog;
